import { Link } from "react-router-dom";

/**
 * Cache-bust the logo asset URLs whenever the artwork is refreshed.
 * v=11 (2026-02-14): dark-mode halo + amber outlined ™ trademark mark.
 */
const ASSET_V = "11";
const ICON_URL = `/icon-512.png?v=${ASSET_V}`;
const HERO_URL = `/teebee-logo-vertical.png?v=${ASSET_V}`;
const HORIZONTAL_URL = `/teebee-logo-horizontal.png?v=${ASSET_V}`;

/**
 * TeeBee logo component.
 *
 * Sizes: sm (24px), md (32px), lg (40px), xl (56px).
 *
 * Variants:
 *   - default     : icon + "TeeBee™" wordmark + "by Factskoot" eyebrow
 *   - minimal     : icon + "TeeBee™" wordmark only
 *   - icon        : icon only
 *   - hero        : full vertical lockup artwork (transparent PNG) with absolute ™
 *   - horizontal  : full horizontal lockup artwork (transparent PNG) with absolute ™
 *
 * Asset assets are TRANSPARENT-BACKGROUND PNGs (v=11) so the logo renders cleanly
 * on both light and dark surfaces. The ™ trademark mark is rendered via JSX (not
 * baked into PNG) so it scales crisply with the surrounding text/image.
 */
export default function Logo({ size = "md", to = "/", showText = true, variant = "default", className = "" }) {
  const iconDim = {
    sm: "h-7 w-7",
    md: "h-9 w-9",
    lg: "h-11 w-11",
    xl: "h-14 w-14",
  }[size] || "h-9 w-9";

  const textSize = {
    sm: "text-base",
    md: "text-xl",
    lg: "text-2xl",
    xl: "text-3xl",
  }[size] || "text-xl";

  /* ───── HERO (full vertical lockup) ───── */
  if (variant === "hero") {
    const Hero = (
      <span className={`relative inline-block ${className}`} data-testid="teebee-logo-hero-wrap">
        {/* LIGHT MODE — full vertical lockup PNG */}
        <span className="block dark:hidden relative">
          <img
            src={HERO_URL}
            alt="TeeBee — Global AI Powered OS & Marketplace for Educators · by Factskoot"
            className="block w-full max-w-[340px] h-auto tb-logo-art"
            data-testid="teebee-logo-hero"
          />
          <span
            aria-hidden
            className="tb-logo-tm absolute select-none pointer-events-none"
            style={{ top: "60%", left: "80%" }}
            data-testid="hero-tm-component"
          >™</span>
        </span>

        {/* DARK MODE — clean icon-only PNG + HTML wordmark for crisp contrast */}
        <span className="hidden dark:flex flex-col items-center w-full max-w-[340px]">
          <span className="relative">
            <span className="absolute inset-0 rounded-full bg-amber-400/30 blur-3xl pointer-events-none" />
            <span className="absolute inset-0 rounded-full bg-violet-400/25 blur-2xl pointer-events-none" />
            <img
              src={ICON_URL}
              alt="TeeBee"
              className="relative h-32 w-32 sm:h-36 sm:w-36 object-contain tb-logo-art"
              draggable={false}
            />
          </span>
          <span className="mt-3 font-display text-4xl sm:text-5xl tracking-tight font-medium leading-none">
            <span className="text-white">Tee</span>
            <span className="bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">Bee</span>
            <span
              aria-hidden
              className="tb-logo-tm tb-logo-tm-sm relative inline-block align-super -top-2"
              style={{ marginLeft: "2px" }}
            >™</span>
          </span>
          <span className="mt-1.5 text-[10px] uppercase tracking-[0.3em] text-white/55 font-medium">by Factskoot</span>
          <span className="mt-2 text-xs sm:text-sm text-white/80 text-center">Global AI Powered OS &amp; Marketplace for Educators</span>
        </span>
      </span>
    );
    return to ? <Link to={to} className="block">{Hero}</Link> : Hero;
  }

  /* ───── HORIZONTAL (header-band lockup) ───── */
  if (variant === "horizontal") {
    const Horiz = (
      <span className={`relative inline-block ${className}`} data-testid="teebee-logo-horizontal-wrap">
        <img
          src={HORIZONTAL_URL}
          alt="TeeBee by Factskoot — AI OS for Educators"
          className="block h-12 w-auto tb-logo-art"
          data-testid="teebee-logo-horizontal"
        />
        <span
          aria-hidden
          className="tb-logo-tm tb-logo-tm-sm absolute select-none pointer-events-none"
          style={{ top: "60%", left: "80%" }}
        >™</span>
      </span>
    );
    return to ? <Link to={to} className="inline-flex">{Horiz}</Link> : Horiz;
  }

  /* ───── ICON ONLY ───── */
  const Icon = (
    <img
      src={ICON_URL}
      alt="TeeBee"
      className={`${iconDim} object-contain shrink-0`}
      data-testid="teebee-logo-icon"
    />
  );

  if (variant === "icon" || !showText) {
    return to ? <Link to={to} className="inline-flex">{Icon}</Link> : Icon;
  }

  /* ───── DEFAULT / MINIMAL (icon + text wordmark with ™) ───── */
  const trademarkSize = {
    sm: "text-[8px]",
    md: "text-[9px]",
    lg: "text-[10px]",
    xl: "text-[11px]",
  }[size] || "text-[9px]";

  const Inner = (
    <span className={`flex items-center gap-2 ${className}`} data-testid="teebee-logo">
      {Icon}
      <span className={`font-display font-semibold tracking-tight ${textSize} leading-none`}>
        <span className="inline-flex items-baseline">
          Tee<span className="text-violet-600 dark:text-violet-300">Bee</span>
          <span
            className={`${trademarkSize} text-violet-600/70 dark:text-violet-300/80 ml-0.5 font-semibold`}
            aria-hidden
          >™</span>
        </span>
        {variant === "default" && (
          <span className="block text-[9px] uppercase tracking-[0.22em] text-muted-foreground font-medium mt-0.5">
            by Factskoot
          </span>
        )}
      </span>
    </span>
  );

  return to ? <Link to={to} className="inline-flex">{Inner}</Link> : Inner;
}
